/**
 * ServiceDetailDialog - modal dialog showing full service details
 * Two-column layout matching mockup, full screen scrollable
 */
import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  ExternalLink,
  Users,
  Settings,
  Link as LinkIcon,
  Package,
  Tag,
  Target,
  Sparkles,
  FileText,
  AlertCircle,
  Globe,
  Pencil,
  Trash2,
} from 'lucide-react'
import { useServiceDetail } from '@/api/service-catalog'
import { usePermissions } from '@/hooks/usePermissions'
import { ServiceFormDialog } from './ServiceFormDialog'
import { DeleteServiceDialog } from './DeleteServiceDialog'

interface ServiceDetailDialogProps {
  /** Name of the service to display, or null if dialog is closed */
  serviceName: string | null
  /** Called when the dialog should close */
  onClose: () => void
}

/**
 * Dialog component that displays full details for a selected service.
 * Uses two-column layout matching the mockup design.
 */
export function ServiceDetailDialog({
  serviceName,
  onClose,
}: ServiceDetailDialogProps) {
  const { data: service, isLoading, error } = useServiceDetail(serviceName)
  const { hasPermission } = usePermissions()
  const canEdit = hasPermission('config:edit')

  // Admin dialog state
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // Detect stale data from previously selected service to avoid showing wrong country
  const isStaleData = service && service.name !== serviceName
  const showLoading = isLoading || isStaleData

  return (
    <Dialog open={!!serviceName} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl w-[90vw] h-[90vh] flex flex-col p-0">
        {showLoading && (
          <div className="flex items-center justify-center h-full">
            {/* Radix requires a Title + Description on every DialogContent, including
                the loading/error states that render before the service is available. */}
            <DialogTitle className="sr-only">Loading service details</DialogTitle>
            <DialogDescription className="sr-only">Fetching service information.</DialogDescription>
            <LoadingSpinner size="lg" message="Loading service details..." />
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <DialogTitle className="sr-only">Failed to load service</DialogTitle>
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <p className="text-lg font-medium">Failed to load service</p>
            <DialogDescription className="text-sm text-muted-foreground mt-1">
              {error instanceof Error ? error.message : 'Unknown error'}
            </DialogDescription>
          </div>
        )}

        {service && !isStaleData && (
          <>
            {/* Header */}
            <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
              <div className="flex items-center justify-between">
                <DialogTitle className="text-2xl font-bold">{service.name}</DialogTitle>
                <Badge variant="outline" className="text-sm inline-flex items-center gap-1">
                  <Globe className="h-3 w-3" />
                  {service.country || 'Global'}
                </Badge>
              </div>
              {/* Always render a DialogDescription so DialogContent stays accessible even
                  when the service has no aliases (visually hidden in that case). */}
              <DialogDescription className={(service.aliases?.length ?? 0) > 0 ? 'text-base' : 'sr-only'}>
                {(service.aliases?.length ?? 0) > 0
                  ? `Aliases: ${service.aliases!.join(', ')}`
                  : `Details for ${service.name}`}
              </DialogDescription>
              {canEdit && (
                <div className="flex items-center gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowEditDialog(true)}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDeleteDialog(true)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    Delete
                  </Button>
                </div>
              )}
            </DialogHeader>

            {/* Scrollable Content */}
            <ScrollArea className="flex-1">
              <div className="p-6 space-y-8">
                {/* Description */}
                <section>
                  <h3 className="text-base font-semibold flex items-center gap-2 mb-3">
                    <FileText className="h-5 w-5" />
                    Description
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {service.description || '—'}
                  </p>
                </section>

                <Separator />

                {/* Purpose */}
                <section>
                  <h3 className="text-base font-semibold flex items-center gap-2 mb-3">
                    <Target className="h-5 w-5" />
                    Purpose
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {service.purpose || '—'}
                  </p>
                </section>

                <Separator />

                {/* Key Features */}
                <section>
                  <h3 className="text-base font-semibold flex items-center gap-2 mb-3">
                    <Sparkles className="h-5 w-5" />
                    Key Features
                  </h3>
                  {(service.keyFeatures?.length ?? 0) > 0 ? (
                    <ul className="space-y-2">
                      {service.keyFeatures!.map((feature, index) => (
                        <li key={index} className="text-sm text-muted-foreground flex gap-3">
                          <span className="text-primary font-bold">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">—</p>
                  )}
                </section>

                <Separator />

                {/* Two-column: Ownership & Technical */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Ownership */}
                  <section className="border rounded-lg p-4">
                    <h3 className="text-base font-semibold flex items-center gap-2 mb-4">
                      <Users className="h-5 w-5" />
                      Ownership
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <span className="text-xs text-muted-foreground uppercase tracking-wide">Team</span>
                        <p className="text-sm font-medium">{service.ownerTeam || '—'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground uppercase tracking-wide">Contacts</span>
                        {(service.ownerContacts?.length ?? 0) > 0 ? (
                          <ul className="mt-1">
                            {service.ownerContacts!.map((contact) => (
                              <li key={contact} className="text-sm">• {contact}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm">—</p>
                        )}
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground uppercase tracking-wide">Area Path</span>
                        <p className="text-sm font-mono">{service.areaPath || '—'}</p>
                      </div>
                    </div>
                  </section>

                  {/* Technical */}
                  <section className="border rounded-lg p-4">
                    <h3 className="text-base font-semibold flex items-center gap-2 mb-4">
                      <Settings className="h-5 w-5" />
                      Technical
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <span className="text-xs text-muted-foreground uppercase tracking-wide">Repository</span>
                        {service.repository ? (
                          <p className="text-sm">
                            <a
                              href={service.repository}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline inline-flex items-center gap-1"
                            >
                              {service.repository.split('/').pop() || 'Repository'}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </p>
                        ) : (
                          <p className="text-sm">—</p>
                        )}
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground uppercase tracking-wide">Wiki</span>
                        {service.wikiPage ? (
                          <p className="text-sm">
                            <a
                              href={service.wikiPage}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline inline-flex items-center gap-1"
                            >
                              Documentation
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </p>
                        ) : (
                          <p className="text-sm">—</p>
                        )}
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground uppercase tracking-wide">Tech Stack</span>
                        {(service.techStack?.length ?? 0) > 0 ? (
                          <p className="text-sm">{service.techStack!.join(', ')}</p>
                        ) : (
                          <p className="text-sm">—</p>
                        )}
                      </div>
                    </div>
                  </section>
                </div>

                <Separator />

                {/* Two-column: Dependencies & Components */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Dependencies */}
                  <section className="border rounded-lg p-4">
                    <h3 className="text-base font-semibold flex items-center gap-2 mb-4">
                      <LinkIcon className="h-5 w-5" />
                      Dependencies
                    </h3>
                    {(service.dependencies?.length ?? 0) > 0 ? (
                      <ul className="space-y-1">
                        {service.dependencies!.map((dep) => (
                          <li key={dep} className="text-sm text-muted-foreground">• {dep}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">—</p>
                    )}
                  </section>

                  {/* Components */}
                  <section className="border rounded-lg p-4">
                    <h3 className="text-base font-semibold flex items-center gap-2 mb-4">
                      <Package className="h-5 w-5" />
                      Components
                    </h3>
                    {(service.components?.length ?? 0) > 0 ? (
                      <ul className="space-y-1">
                        {service.components!.map((component) => (
                          <li key={component} className="text-sm text-muted-foreground">• {component}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">—</p>
                    )}
                  </section>
                </div>

                <Separator />

                {/* Keywords */}
                <section>
                  <h3 className="text-base font-semibold flex items-center gap-2 mb-3">
                    <Tag className="h-5 w-5" />
                    Keywords
                  </h3>
                  {(service.keywords?.length ?? 0) > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {service.keywords!.map((keyword) => (
                        <Badge key={keyword} variant="secondary">
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">—</p>
                  )}
                </section>
              </div>
            </ScrollArea>
          </>
        )}
      </DialogContent>

      {/* Admin Dialogs */}
      {service && (
        <>
          <ServiceFormDialog
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            mode="edit"
            service={service}
          />
          <DeleteServiceDialog
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            serviceName={service.name}
            onSuccess={onClose}
          />
        </>
      )}
    </Dialog>
  )
}
